import React, { useCallback, useEffect } from "react";
import { Card, Chip, Typography, Button, IconButton, Box } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTheme } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import type { DynamicCase } from "../../types";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  createDiagnosticSession,
  addSystemMessage,
  endDiagnosticSession,
} from "../../store/thunks/sessionThunks";
import { clearDiagnosisCompleteness } from "../../store/slices/diagnosisSlice";
import { selectCurrentSessionWithDetails } from "../../store/selectors";

interface Props {
  patientCase: DynamicCase;
  onViewDetails: (c: DynamicCase) => void;
  selectedField?: string;
}

export const PatientCaseCard: React.FC<Props> = ({
  patientCase,
  onViewDetails,
  selectedField,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const sessionDetails = useAppSelector(selectCurrentSessionWithDetails);

  const difficulty = getDifficulty(patientCase);

  /** Create session → then route */
  const handleCaseSelect = useCallback(
    async (caseId: string) => {
      if (!selectedField) {
        console.error("No field selected");
        return;
      }

      console.log("selectedField", selectedField);

      // Already on this case?
      if (sessionDetails?.case?.id === caseId) return;

      try {
        // Create the session and get the response with session ID
        const result = await dispatch(
          createDiagnosticSession({ fieldId: selectedField, caseId })
        ).unwrap();

        dispatch(clearDiagnosisCompleteness());

        console.log("Session creation result:", result);

        if (result?.sessionId) {
          // Navigate with the session ID from the response
          navigate(
            `/diagnostic/${caseId}/${selectedField}/${result.sessionId}`
          );
        } else {
          console.error("No session ID available after creation");
          dispatch(
            addSystemMessage("Failed to create session - no session ID")
          );
        }
      } catch (err) {
        console.error("❌ Session creation failed:", err);
        dispatch(addSystemMessage(`Failed to load case: ${err}`));
      }
    },
    [dispatch, selectedField, sessionDetails?.case, navigate]
  );

  // Helper function to get processing status color
  const getProcessingStatusColor = (
    status: DynamicCase["processing_status"]
  ) => {
    switch (status) {
      case "completed":
        return "#22c55e";
      case "processing":
        return "#f59e0b";
      case "pending":
        return "#6b7280";
      case "failed":
        return "#ef4444";
      case "review_required":
        return "#8b5cf6";
      default:
        return "#6b7280";
    }
  };

  // Check if case can be started (only completed cases)
  const canStartCase = patientCase.processing_status === "completed";

  /* ---------- UI ---------- */
  return (
    <Box
      key={patientCase.id}
      className="case-card"
      sx={{ backgroundColor: "background.paper", height: "100%" }}
    >
      <Box className="case-header">
        <Typography
          variant="h6"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: "text.primary",
            height: "60px",
          }}
        >
          {patientCase.title || "Untitled Case"}
        </Typography>
        <span className={`difficulty-badge ${getDifficulty(patientCase)}`}>
          {getDifficulty(patientCase)}
        </span>
      </Box>
      <Box className="case-preview" sx={{ color: "#9ca3af" }}>
        <p
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            height: "50px",
          }}
        >
          {patientCase.preview || "No preview available"}
        </p>
      </Box>
      <Box
        className="case-details"
        sx={{
          backgroundColor: (theme) => theme.palette.grey[100],
          borderRadius: "8px",
          padding: "10px",
          marginTop: "10px",
        }}
      >
        <Box className="detail-item" sx={{ color: "text.primary" }}>
          <span className="detail-label">Age:</span>
          <span className="detail-value">
            {patientCase.age ? `${patientCase.age} years` : "Not specified"}
          </span>
        </Box>
        <Box className="detail-item" sx={{ color: "text.primary" }}>
          <span className="detail-label">Gender:</span>
          <span className="detail-value">
            {patientCase.gender || "Not specified"}
          </span>
        </Box>

        {/* <Box className="detail-item">
          <span className="detail-label">Status:</span>
          <span 
            className="detail-value"
            style={{
              color: getProcessingStatusColor(patientCase.processing_status),
              fontWeight: 'bold',
              textTransform: 'capitalize'
            }}
          >
            {patientCase.processing_status}
          </span>
        </Box> */}
      </Box>

      {/* Processing Status Indicator */}
      {/* <div style={{ marginBottom: '12px' }}>
        <Chip
          label={patientCase.processing_status}
          size="small"
          sx={{
            backgroundColor: getProcessingStatusColor(patientCase.processing_status),
            color: 'white',
            fontWeight: 500,
            textTransform: 'capitalize',
            fontSize: '11px'
          }}
        />
        {patientCase.difficulty_level && (
          <Chip
            label={patientCase.difficulty_level}
            size="small"
            sx={{
              ml: 1,
              backgroundColor: '#e2e8f0',
              color: '#475569',
              fontWeight: 500,
              textTransform: 'capitalize',
              fontSize: '11px'
            }}
          />
        )}
      </div> */}

      <Box sx={{ display: "flex", gap: "12px", marginTop: "16px" }}>
        <Button
          variant="contained"
          onClick={() => handleCaseSelect(patientCase.id)}
          sx={{
            flex: 9,
            height: "48px",
            backgroundColor: "primary.main",
            color: "text.primary",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
        >
          Start Diagnosis
        </Button>
        <IconButton
          onClick={() => onViewDetails(patientCase)}
          style={{
            flex: 1,
            height: "48px",
            border: `2px solid ${theme.palette.primary.main}`,
            borderRadius: "8px",
            backgroundColor: "transparent",
            color: theme.palette.primary.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            minWidth: "48px",
          }}
        >
          <InfoOutlinedIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

/* helper */
function getDifficulty(c: DynamicCase) {
  const lvl = c.difficulty_level;
  if (lvl === "advanced") return "Advanced";
  if (lvl === "intermediate") return "Intermediate";
  return "Beginner";
}
